import { useEffect } from "react";
import { FieldValues, UseControllerProps, useWatch } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { CPTCode } from "services/cpt-codes";

import { renderCPTCodeOption } from "./cpt-code-option";
import { useCPTCodeSearch } from "./use-cpt-code-search.hook";

type CPTCodeFieldProps = {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onOptionSelected?: (option: CPTCode | null) => void;
  placeholder?: string;
  sx?: SxProps;
};

export type RhfCPTCodeField<T extends FieldValues> = CPTCodeFieldProps &
  UseControllerProps<T>;

export default function CPTCodeField<T extends FieldValues>({
  control,
  required,
  disabled,
  name,
  label = "CPT or HCPCS Code",
  onOptionSelected,
  placeholder = "Search CPT or HCPCS Code by code or description",
  sx = {}
}: RhfCPTCodeField<T>) {
  const { options, isLoading, search, setSearch } = useCPTCodeSearch();

  const cptCodeOption = useWatch({ control, name }) as CPTCode;

  useEffect(() => {
    setSearch(String(cptCodeOption?.code ?? ""));
  }, [cptCodeOption?.code, setSearch]);

  return (
    <AutocompleteFormField
      isLoading={isLoading}
      required={required}
      disabled={disabled}
      openOnFocus
      name={name}
      label={label}
      options={options as CPTCode[]}
      noOptionsText={!search?.length ? placeholder : "No options"}
      getOptionLabel={({ code }: CPTCode) => code}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      placeholder={placeholder}
      renderOption={renderCPTCodeOption}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      onOptionSelected={(option: any) => {
        if (!option) {
          setSearch("");
        }
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={sx}
    />
  );
}
